import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ApolloError } from '@apollo/client';
import { useGetEventByNameForPeoplePickerQuery } from './usePeoplePickerButtonController.graphql.generated';
import type { PersonFragment } from '@graphql/fragments/person.graphql.generated';
import type { LabelIdsToPeopleIdsMap, LabelViewItem, PersonViewItem, PersonItemRender } from './PeoplePicker';
import loadable from '@loadable/component';
import { useOverlayUnstable } from '@withjoy/joykit';
import { getInGroupPosition } from '@shared/utils/groupedListUtils';
import { isEqual } from 'lodash-es';
import { useTranslation } from '@shared/core';
import type { DeliveryMethodValue, PeoplePickerUseCase } from './PeoplePicker.types';
import { usePeopleContext } from '@shared/context/people';
import { hasValidPhone } from '@shared/utils/messaging/deliveryChannel';
import { getPersonName, getAvatarInitials } from '@shared/components/PeopleListItem/PeopleListItem.utils';
import { getAvatarUrl } from '@shared/utils/getAvatarUrl';
import { persistPreference } from '@shared/utils/messaging/deliveryChannel';
import { isPersonDisabled, getSublabelText } from './PeoplePicker.utils';

const PeoplePickerDialog = loadable(() => import('./PeoplePicker.dialog'));

export type SelectedPerson = Readonly<Partial<PersonFragment & { full_name: string }>>;

export type HardcodedLabel = Readonly<{
  id: string;
  display: string;
  people: ReadonlyArray<{ id: string; personIdTag: string }>;
  initiallySelected?: boolean;
  labelDescription?: string;
}>;

export type StoredSelectedPerson = SelectedPerson &
  Readonly<{
    hasEmailDeliveryMethodSelected?: boolean;
    hasTextDeliveryMethodSelected?: boolean;
    personName?: string;
    avatarInitials?: string;
    avatarUrl?: string;
  }>;

type UsePeoplePickerButtonControllerProps = Readonly<{
  title: React.ReactNode;
  eventHandle: string;
  onSelectedPeople?: (people: ReadonlyArray<StoredSelectedPerson>) => void;
  enableSmsSelection?: boolean;
  smsOnly?: boolean;
  emailOnly?: boolean;
  initialDeliveryMethod?: DeliveryMethodValue;
  /** When provided, persists the "if both are available, send via:" preference to sessionStorage. */
  preferenceStorageKey?: string;
  /** When true, people missing contact info for the delivery method can still be selected/deselected. */
  allowUnreachableSelection?: boolean;
  /** When true, renders the delivery method segmented controller inside the people picker dialog. */
  showDeliveryMethodSelector?: boolean;
  hardcodedLabels?: ReadonlyArray<HardcodedLabel>;
  personItemRender?: PersonItemRender;
  useCase: PeoplePickerUseCase;
}>;

type UsePeoplePickerButtonControllerReturn = Readonly<{
  loading: boolean;
  error: ApolloError | undefined;
  selectedPeople: ReadonlyArray<StoredSelectedPerson>;
  startSelection: () => void;
  viewPeople: ReadonlyArray<PersonViewItem>;
  viewLabels: ReadonlyArray<LabelViewItem>;
}>;

export const usePeoplePickerButtonController = ({
  eventHandle,
  enableSmsSelection = false,
  smsOnly = false,
  emailOnly = false,
  initialDeliveryMethod,
  preferenceStorageKey,
  allowUnreachableSelection = false,
  showDeliveryMethodSelector = false,
  title,
  onSelectedPeople,
  hardcodedLabels,
  personItemRender,
  useCase,
}: UsePeoplePickerButtonControllerProps): UsePeoplePickerButtonControllerReturn => {
  const { tq } = useTranslation('peoplePickerButton');

  const { selectedPeople, setSelectedPeople } = usePeopleContext();
  const [viewLabels, setViewLabels] = useState<ReadonlyArray<LabelViewItem>>([]);
  const [viewPeople, setViewPeople] = useState<ReadonlyArray<PersonViewItem>>([]);
  const [labelIdsToPeopleIdsMap, setLabelIdsToPeopleIdsMap] = useState<LabelIdsToPeopleIdsMap>({});
  const [hasStartedSelection, setHasStartedSelection] = useState(false);

  // --- Refs to decouple delivery-method / delivery-flag changes from viewPeople recomputation ---
  // The providers already recompute delivery flags on selectedPeople via recomputeDeliveryFlags;
  // the dialog re-derives disabled states via REINITIALIZE_STATE on open.
  // Using refs here prevents the cascade: deliveryMethod change → viewPeople change →
  // setSelectedPeople → viewPeople change → overlay re-registration loop.
  const initialDeliveryMethodRef = useRef(initialDeliveryMethod);
  initialDeliveryMethodRef.current = initialDeliveryMethod;

  const selectedPeopleRef = useRef(selectedPeople);
  selectedPeopleRef.current = selectedPeople;

  // Stable fingerprint that changes only when the set of selected person IDs changes,
  // NOT when delivery flags are recomputed on existing selections.
  const selectedIdsKey = useMemo(
    () =>
      selectedPeople
        .map(p => p.id ?? p.personIdTag ?? '')
        .sort()
        .join(','),
    [selectedPeople],
  );

  const { data, error, loading } = useGetEventByNameForPeoplePickerQuery({
    batchMode: undefined,
    variables: { eventHandle },
  });

  // only api labels are filtered out if they have no people
  const apiLabels = useMemo<ReadonlyArray<HardcodedLabel>>(
    () => hardcodedLabels || data?.eventByName?.labels.filter(label => label.people && label.people.length > 0) || [],
    [data, hardcodedLabels],
  );

  const apiHouseholds = useMemo(() => {
    return data?.eventByName?.guests.households || [];
  }, [data]);

  const isPersonSelected = useCallback(
    (person: PersonFragment): boolean => {
      return selectedPeopleRef.current.some(selectedPerson => person.id === selectedPerson.id || person.personIdTag === selectedPerson.personIdTag);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reads from ref; selectedIdsKey ensures re-creation when selection membership changes
    [selectedIdsKey],
  );

  const getMatchingStoredSelectedPerson = useCallback(
    (person: PersonFragment): StoredSelectedPerson | undefined => {
      return selectedPeopleRef.current.find(selectedPerson => person.id === selectedPerson.id || person.personIdTag === selectedPerson.personIdTag);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reads from ref; selectedIdsKey ensures re-creation when selection membership changes
    [selectedIdsKey],
  );

  useEffect(() => {
    const newLabelIdsToPeopleIdsMap: Record<string, Array<string>> = {};
    const newViewLabels = apiLabels.map((label): LabelViewItem => {
      newLabelIdsToPeopleIdsMap[label.id] = label.people.map(person => person.id);
      return {
        id: label.id,
        selected: label.people.length > 0 && label.people.every(person => selectedPeopleRef.current.some(sp => sp.id === person.id || sp.personIdTag === person.personIdTag)),
        labelText: `${label.display} (${label.people.length})`,
        disabled: label.people.length === 0,
        labelDescription: label.labelDescription,
      };
    });

    const newViewPeople = apiHouseholds.reduce((acc, household) => {
      const leaderFirstName = household.members[0]?.firstName;
      const householdPersons = household.members.map((person, index): PersonViewItem => {
        const hasValidPhoneNumber = hasValidPhone(person.phoneDisplay);

        const isDisabled = isPersonDisabled({
          hasEmail: !!person.hasEmail,
          hasPhone: hasValidPhoneNumber,
          emailOnly,
          smsOnly,
          allowUnreachableSelection,
          enableSmsSelection,
          deliveryMethod: initialDeliveryMethodRef.current,
        });
        const storedSelectedPerson = getMatchingStoredSelectedPerson(person);

        const selected = isPersonSelected(person);
        const fullName = [person.firstName, person.lastName].filter(name => !!name).join(' ');
        const labelText = fullName || (index > 0 && leaderFirstName ? tq`${leaderFirstName}'s Guest` : '');
        return {
          id: person.id,
          personIdTag: person.personIdTag || undefined,
          selected,
          labelText,
          firstName: person.firstName ?? undefined,
          lastName: person.lastName ?? undefined,
          sublabelText: getSublabelText({ smsOnly, emailOnly, hasPhone: hasValidPhoneNumber, enableSmsSelection, hasEmail: !!person.hasEmail, tq }),
          disabled: isDisabled,
          groupId: person.household!.id,
          avatarName: [person.firstName, person.lastName].filter(name => !!name).join(' '),
          avatarUrl: getAvatarUrl(person),
          inGroupPosition: getInGroupPosition(index, household.members.length),
          email: person.email || '',
          phoneDisplay: person.phoneDisplay ?? undefined,
          hasPhoneNumber: hasValidPhoneNumber,
          hasEmailDeliveryMethodSelected: storedSelectedPerson?.hasEmailDeliveryMethodSelected ?? (selected && person.hasEmail && initialDeliveryMethodRef.current !== 'text'),
          hasTextDeliveryMethodSelected:
            storedSelectedPerson?.hasTextDeliveryMethodSelected ?? (enableSmsSelection && selected && hasValidPhoneNumber && initialDeliveryMethodRef.current !== 'email'),
        };
      });
      return [...acc, ...householdPersons];
    }, [] as ReadonlyArray<PersonViewItem>);

    if (!isEqual(newViewLabels, viewLabels)) {
      setViewLabels(newViewLabels);
    }
    if (!isEqual(newViewPeople, viewPeople)) {
      setViewPeople(newViewPeople);
    }
    if (!isEqual(newLabelIdsToPeopleIdsMap, labelIdsToPeopleIdsMap)) {
      setLabelIdsToPeopleIdsMap(newLabelIdsToPeopleIdsMap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedPeople read via ref; selectedIdsKey triggers when selection membership changes
  }, [
    enableSmsSelection,
    apiLabels,
    apiHouseholds,
    isPersonSelected,
    selectedIdsKey,
    setViewLabels,
    setViewPeople,
    viewLabels,
    viewPeople,
    labelIdsToPeopleIdsMap,
    getMatchingStoredSelectedPerson,
    tq,
    smsOnly,
    emailOnly,
    allowUnreachableSelection,
  ]);

  // Enrich person ids when passing selectedPeople array from local storage (it only has personIdTags, we need ids).
  // Uses a functional updater so that delivery flags set by a provider's setDeliveryMethod
  // (which may have landed between the viewPeople recompute and this effect) are preserved.
  useEffect(() => {
    if (!viewPeople?.length) return;

    const enrichedFromView = viewPeople
      .filter(person => person.selected)
      .map(person => ({
        id: person.id,
        personIdTag: person.personIdTag,
        full_name: person.labelText,
        email: person.email,
        phoneDisplay: person.phoneDisplay,
        firstName: person.firstName,
        lastName: person.lastName,
        personName: getPersonName(person),
        avatarInitials: getAvatarInitials(person),
        avatarUrl: person.avatarUrl,

        // Need to retain the two following for use in `useSendEmailHandlers`
        hasEmailDeliveryMethodSelected: person.hasEmailDeliveryMethodSelected,
        hasTextDeliveryMethodSelected: person.hasTextDeliveryMethodSelected,
      }));

    setSelectedPeople(prev => {
      // Preserve delivery flags from prev when defined — a provider's setDeliveryMethod
      // may have updated them after viewPeople was computed, so prev is more authoritative.
      // Fall through to enriched values when prev flags are undefined (sparse initial selection).
      const prevById = new Map(prev.map(p => [p.id ?? p.personIdTag, p]));
      const merged = enrichedFromView.map(person => {
        const existing = prevById.get(person.id ?? person.personIdTag);
        if (existing) {
          return {
            ...person,
            hasEmailDeliveryMethodSelected: existing.hasEmailDeliveryMethodSelected ?? person.hasEmailDeliveryMethodSelected,
            hasTextDeliveryMethodSelected: existing.hasTextDeliveryMethodSelected ?? person.hasTextDeliveryMethodSelected,
          };
        }
        return person;
      });
      return isEqual(prev, merged) ? prev : merged;
    });
  }, [viewPeople, setSelectedPeople]);

  const submit = ({ mergedSelectionPeople, preference }: { mergedSelectionPeople: ReadonlyArray<PersonViewItem>; preference?: DeliveryMethodValue }) => {
    if (preferenceStorageKey && preference) {
      persistPreference(preferenceStorageKey, preference);
    }
    const newSelectedPeople = mergedSelectionPeople
      .filter(person => person.selected)
      .map(person => ({
        id: person.id,
        personIdTag: person.personIdTag,
        full_name: person.labelText,
        email: person.email,
        phoneDisplay: person.phoneDisplay,
        firstName: person.firstName,
        lastName: person.lastName,
        personName: getPersonName(person),
        avatarInitials: getAvatarInitials(person),
        avatarUrl: person.avatarUrl,

        // Collect the following two fields to determine what method we should use to contact the person
        hasEmailDeliveryMethodSelected: person.hasEmailDeliveryMethodSelected,
        hasTextDeliveryMethodSelected: person.hasTextDeliveryMethodSelected,
      }));
    if (onSelectedPeople) {
      onSelectedPeople(newSelectedPeople);
    }
    // it is used only specifically for the invitations/save the dates case, as no labels selection should be saved - only people
    setSelectedPeople(newSelectedPeople);
    setHasStartedSelection(false);
    // this should be the last step, since unmounting the component create a race condition and stale data of selected people
    hidePeoplePicker();
  };

  const cancel = () => {
    setHasStartedSelection(false);
    hidePeoplePicker();
  };

  const [showPeoplePicker, hidePeoplePicker] = useOverlayUnstable(
    {
      overlayKey: 'people_picker',
      render: overlayProps => {
        if (hasStartedSelection) {
          return (
            <PeoplePickerDialog
              enableSmsSelection={enableSmsSelection}
              smsOnly={smsOnly}
              emailOnly={emailOnly}
              initialDeliveryMethod={initialDeliveryMethod}
              showDeliveryMethodSelector={showDeliveryMethodSelector}
              tagsTitle={hardcodedLabels ? tq`Filters` : tq`Tags`}
              personItemRender={personItemRender}
              {...overlayProps}
            />
          );
        }

        return <></>;
      },
      props: {
        title,
        eventHandle,
        allPeople: viewPeople,
        allLabels: viewLabels,
        labelIdsToPeopleIdsMap,
        submit,
        cancel,
        useCase,
        hasStartedSelection,
      },
    },
    { dependencies: [viewPeople, viewLabels, hasStartedSelection, initialDeliveryMethod] },
  );

  const startSelection = useCallback(() => {
    setHasStartedSelection(true);
    showPeoplePicker();
  }, [showPeoplePicker]);

  return {
    loading,
    error,
    selectedPeople,
    startSelection,
    viewPeople,
    viewLabels,
  };
};
